import { FC, useEffect, useMemo, useRef } from 'react';

import { acquisitionsQueries, RequirementAvailabilityIndexPayload } from '@agency/apis/acquisitions';
import { DisposalRecordFloorUnit } from '@agency/apis/disposals';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Button } from '@shared/components/button-new';
import { Loading } from '@shared/components/loading';
import { RadioGroup, RadioGroupOption, RadioGroupSerializerType } from '@shared/components/radio-group';
import { RecordSpaceItemError, RecordSpacesTakenForm } from '@shared/components/record-spaces-taken-form';
import { SingleSelect } from '@shared/components/select-new/single-select';
import { useAgencyConstants } from '@shared/contexts/ConfigContext';
import { useAvailabilityScheduleConfig } from '@shared/hooks/availabilitySchedule.hooks';
import { useAxiosQuery } from '@shared/hooks/axios';
import { ButtonSize, NewButtonColour } from '@shared/types/common/button';

import { styles } from './RequirementRecordSpaceCard.styles';
import {
  convertAvailabilityScheduleSpacesToFloorUnits,
  getMatchScheduleMatchOptions,
} from '../../RequirementRecordDrawer.utils';

const getSpaceRadioOptions = (hasShortlistOptions: boolean): RadioGroupOption[] => [
  {
    id: '0',
    label: t('the_property_is_on_my_shortlist'),
    disabled: !hasShortlistOptions,
  },
  {
    id: '1',
    label: t('the_property_is_not_on_my_shortlist'),
  },
];

export interface RequirementSpaceData {
  buildingTypes: string[];
  entireProperty: StringBoolean;
  floorUnits: DisposalRecordFloorUnit[];
  onShortlist: string;
  matchIdSelected: string;
  sizeMeasure?: string;
  sizeMetric: Metric;
  sizeValue: string;
}

export interface RequirementSpaceErrors {
  addressError: boolean;
  buildingTypesError: boolean;
  errorDisposal: boolean;
  onShortlistError: boolean;
  sizeError: boolean;
  spaceCardErrors: Record<number, RecordSpaceItemError>;
}

export interface RequirementRecordSpaceCardProps extends WithStyles<typeof styles> {
  data: RequirementSpaceData;
  errors: RequirementSpaceErrors;
  propertyAddress?: string;
  requirementId: string;
  investment: boolean;
  onAddAddress?: () => void;
  onEditAddress?: () => void;
  onResetAddress?: () => void;
  onUpdateData: (data: Partial<RequirementSpaceData>) => void;
}

const RequirementRecordSpaceCardComponent: FC<RequirementRecordSpaceCardProps> = ({
  classes,
  data,
  errors,
  propertyAddress,
  requirementId,
  investment,
  onAddAddress,
  onEditAddress,
  onResetAddress,
  onUpdateData,
}) => {
  const { constants } = useAgencyConstants();
  const availabilityScheduleConfig = useAvailabilityScheduleConfig(investment);
  const hasPopulatedInitialMatch = useRef(false);

  const availabilityIndexPayload: RequirementAvailabilityIndexPayload = useMemo(
    () => ({
      acquisitionId: requirementId,
      query: {},
    }),
    [requirementId]
  );
  const requirementAvailabilityScheduleListQuery = useAxiosQuery(
    availabilityIndexPayload,
    acquisitionsQueries.availabilityIndex
  );

  const matchOptions = useMemo(() => {
    if (requirementAvailabilityScheduleListQuery.data?.data) {
      return getMatchScheduleMatchOptions(
        availabilityScheduleConfig,
        requirementAvailabilityScheduleListQuery.data.data
      );
    }

    return [];
  }, [availabilityScheduleConfig, requirementAvailabilityScheduleListQuery.data]);

  useEffect(() => {
    if (
      requirementAvailabilityScheduleListQuery.isSuccess &&
      matchOptions.length === 0 &&
      data.onShortlist === '0' &&
      !data.matchIdSelected
    ) {
      onUpdateData({ onShortlist: '1' });
    }
  }, [
    requirementAvailabilityScheduleListQuery.isSuccess,
    matchOptions.length,
    data.onShortlist,
    data.matchIdSelected,
    onUpdateData,
  ]);

  useEffect(() => {
    if (
      data.matchIdSelected &&
      requirementAvailabilityScheduleListQuery.data?.data &&
      !hasPopulatedInitialMatch.current &&
      data.floorUnits.length === 0
    ) {
      const matchId = parseInt(data.matchIdSelected, 10);
      const matchItem = requirementAvailabilityScheduleListQuery.data.data.find((item) => item.id === matchId);

      if (matchItem) {
        hasPopulatedInitialMatch.current = true;
        const availableSpaces = matchItem.available_spaces ?? [];
        const floorUnits = convertAvailabilityScheduleSpacesToFloorUnits(availableSpaces, matchItem.property_id || 0);
        const sizeValue = availableSpaces.reduce((acc, space) => acc + (space.content.size.value ?? 0), 0).toString();
        let sizeMetric: Metric | undefined;
        if (availableSpaces.length > 0) {
          const firstSpaceMetric = availableSpaces[0]?.content?.size?.metric;
          if (firstSpaceMetric) {
            sizeMetric = firstSpaceMetric;
          }
        }

        onUpdateData({
          floorUnits,
          sizeValue,
          ...(sizeMetric && { sizeMetric }),
        });
      }
    }
  }, [data.matchIdSelected, data.floorUnits.length, requirementAvailabilityScheduleListQuery.data, onUpdateData]);

  const selectedMatchPropertyId = useMemo(() => {
    const selectedMatch = requirementAvailabilityScheduleListQuery?.data?.data.find(
      (item) => item.id.toString() === data.matchIdSelected
    );
    return selectedMatch?.property_id || 0;
  }, [requirementAvailabilityScheduleListQuery?.data?.data, data.matchIdSelected]);

  const selectedMatchPropertyAddress = useMemo(() => {
    const selectedMatch = requirementAvailabilityScheduleListQuery?.data?.data.find(
      (item) => item.id.toString() === data.matchIdSelected
    );
    if (selectedMatch) {
      return `${selectedMatch.address_name}${selectedMatch.address ? `, ${selectedMatch.address}` : ''}`;
    }
    return undefined;
  }, [requirementAvailabilityScheduleListQuery?.data?.data, data.matchIdSelected]);

  const handleSelectMatch = (id: number) => {
    let floorUnits: DisposalRecordFloorUnit[] = [];
    let sizeValue = '';
    let sizeMetric: Metric | undefined;

    requirementAvailabilityScheduleListQuery?.data?.data.forEach((item) => {
      if (item.id === id) {
        const availableSpaces = item.available_spaces ?? [];
        floorUnits = convertAvailabilityScheduleSpacesToFloorUnits(availableSpaces, item.property_id || 0);
        sizeValue = availableSpaces.reduce((acc, space) => acc + (space.content.size.value ?? 0), 0).toString();
        if (availableSpaces.length > 0) {
          const firstSpaceMetric = availableSpaces[0]?.content?.size?.metric;
          if (firstSpaceMetric) {
            sizeMetric = firstSpaceMetric;
          }
        }
      }
    });

    onUpdateData({
      matchIdSelected: id.toString(),
      floorUnits,
      sizeValue,
      ...(sizeMetric && { sizeMetric }),
    });
  };

  const tenantOnScheduleContent = useMemo(() => {
    if (requirementAvailabilityScheduleListQuery.isFetching) {
      return <Loading classes={{ root: classes.loading }} />;
    }

    return (
      <div className={classes.tenantOnScheduleContent}>
        <SingleSelect
          error={errors.errorDisposal}
          fullWidth
          options={matchOptions}
          placeholder={t('xtextx_ellipsis', { text: t('choose_property_the_requirement_completed_with') })}
          size={ButtonSize.medium}
          value={data.matchIdSelected}
          onChange={(value) => handleSelectMatch(Number(value))}
        />
        {data.matchIdSelected && (
          <>
            <RecordSpacesTakenForm
              buildingTypes={data.buildingTypes}
              buildingTypesError={errors.buildingTypesError}
              classes={{
                cardHeader: classes.spaceCardHeader,
                addressContainer: classes.hideAddress,
              }}
              disabled={data.entireProperty === '1'}
              entireProperty={Number(data.entireProperty)}
              propertyId={selectedMatchPropertyId}
              propertyAddress={selectedMatchPropertyAddress}
              floorUnits={data.floorUnits}
              sizeMeasure={data.sizeMeasure}
              sizeValue={data.sizeValue}
              sizeMetric={data.sizeMetric}
              spaceCardErrors={errors.spaceCardErrors}
              sizeError={errors.sizeError}
              onAddFloorUnit={() => {
                const updatedFloorUnits = [
                  ...data.floorUnits,
                  {
                    floor_string: '',
                    floor_unit_id: null,
                    floor: '',
                    id: null,
                    include: 1 as NumberBoolean,
                    part_floor: false,
                    property_id: selectedMatchPropertyId,
                    property_types: [],
                    size_metric: constants.defaultSizeMetric,
                    size_value_sqft: null,
                    size_value: '',
                    status: '',
                    unit: '',
                    unit_string: '',
                  },
                ];

                onUpdateData({ floorUnits: updatedFloorUnits });
              }}
              onChangeBuildingTypes={(value) => onUpdateData({ buildingTypes: value as string[] })}
              onChangeEntireProperty={(value: Id) => {
                const includeValue: NumberBoolean = value ? 1 : 0;
                const entirePropertyValue: StringBoolean = value ? '1' : '0';
                onUpdateData({
                  entireProperty: entirePropertyValue,
                  floorUnits: data.floorUnits.map((floorUnit) => ({
                    ...floorUnit,
                    include: includeValue,
                  })),
                });
              }}
              onChangeMeasureType={(sizeMeasure) => onUpdateData({ sizeMeasure })}
              onEditFloorUnit={(floor, idx) => {
                const updatedFloorUnits = data.floorUnits.map((floorUnit, index) => {
                  if (index === idx) {
                    return {
                      ...floorUnit,
                      ...floor,
                    };
                  }
                  return floorUnit;
                });
                onUpdateData({ floorUnits: updatedFloorUnits });
              }}
              onChangeSizeMetric={(metric, floor) => {
                onUpdateData({ sizeMetric: metric });

                if (floor) {
                  const updatedFloorUnits = data.floorUnits.map((f) => ({ ...f, size_metric: metric }));
                  onUpdateData({ floorUnits: updatedFloorUnits });
                }
              }}
              onChangeSizeValue={(metric) => onUpdateData({ sizeValue: metric })}
            />
          </>
        )}
      </div>
    );
  }, [
    constants.defaultSizeMetric,
    errors.sizeError,
    errors.spaceCardErrors,
    errors.errorDisposal,
    data.entireProperty,
    data.matchIdSelected,
    data.floorUnits,
    data.sizeValue,
    data.sizeMetric,
    matchOptions,
    onUpdateData,
    requirementAvailabilityScheduleListQuery.isFetching,
    selectedMatchPropertyId,
    selectedMatchPropertyAddress,
  ]);

  const tenantNotInScheduleContent = useMemo(() => {
    const hasAddress = Boolean(propertyAddress);

    return (
      <div className={classes.noScheduleContent}>
        {onAddAddress && !hasAddress && (
          <Button
            colour={NewButtonColour.Outline}
            size={ButtonSize.small}
            text={t('add_address')}
            onClick={onAddAddress}
            classes={{ root: errors.addressError ? classes.addAddressButtonError : classes.addAddressButton }}
          />
        )}
        {hasAddress && (
          <>
            <RecordSpacesTakenForm
              buildingTypes={data.buildingTypes}
              buildingTypesError={errors.buildingTypesError}
              classes={{
                cardHeader: classes.spaceCardHeader,
              }}
              disabled={data.entireProperty === '1'}
              propertyAddress={propertyAddress}
              entireProperty={Number(data.entireProperty)}
              propertyId={0}
              floorUnits={data.floorUnits}
              sizeMeasure={data.sizeMeasure}
              sizeValue={data.sizeValue}
              sizeMetric={data.sizeMetric}
              spaceCardErrors={errors.spaceCardErrors}
              sizeError={errors.sizeError}
              onAddFloorUnit={() => {
                const updatedFloorUnits = [
                  ...data.floorUnits,
                  {
                    floor_string: '',
                    floor_unit_id: null,
                    floor: '',
                    id: null,
                    include: 1 as NumberBoolean,
                    part_floor: false,
                    property_id: 0,
                    property_types: [],
                    size_metric: constants.defaultSizeMetric,
                    size_value_sqft: null,
                    size_value: '',
                    status: '',
                    unit: '',
                    unit_string: '',
                  },
                ];

                onUpdateData({ floorUnits: updatedFloorUnits });
              }}
              onEditAddress={onEditAddress}
              onChangeBuildingTypes={(value) => onUpdateData({ buildingTypes: value as string[] })}
              onChangeEntireProperty={(value: Id) => {
                const includeValue: NumberBoolean = value ? 1 : 0;
                const entirePropertyValue: StringBoolean = value ? '1' : '0';
                onUpdateData({
                  entireProperty: entirePropertyValue,
                  floorUnits: data.floorUnits.map((floorUnit) => ({
                    ...floorUnit,
                    include: includeValue,
                  })),
                });
              }}
              onChangeMeasureType={(sizeMeasure) => onUpdateData({ sizeMeasure })}
              onEditFloorUnit={(floor, idx) => {
                const updatedFloorUnits = data.floorUnits.map((floorUnit, index) => {
                  if (index === idx) {
                    return {
                      ...floorUnit,
                      ...floor,
                    };
                  }
                  return floorUnit;
                });
                onUpdateData({ floorUnits: updatedFloorUnits });
              }}
              onChangeSizeMetric={(metric, floor) => {
                onUpdateData({ sizeMetric: metric });

                if (floor) {
                  const updatedFloorUnits = data.floorUnits.map((f) => ({ ...f, size_metric: metric }));
                  onUpdateData({ floorUnits: updatedFloorUnits });
                }
              }}
              onChangeSizeValue={(metric) => onUpdateData({ sizeValue: metric })}
            />
          </>
        )}
      </div>
    );
  }, [
    constants.defaultSizeMetric,
    data.entireProperty,
    data.floorUnits,
    data.sizeMetric,
    data.sizeValue,
    errors.addressError,
    errors.sizeError,
    errors.spaceCardErrors,
    onAddAddress,
    onEditAddress,
    onUpdateData,
    propertyAddress,
  ]);

  return (
    <div className={classes.root}>
      <div className={classes.cardHeader}>
        <span className={classes.cardTitle}>{t('property_and_space')}</span>
      </div>
      <RadioGroup
        classes={{
          root: classes.radioRoot,
          radioWrapper: classes.radioWrapper,
        }}
        direction="row"
        error={errors.onShortlistError}
        options={getSpaceRadioOptions(matchOptions.length > 0)}
        serializerType={RadioGroupSerializerType.string}
        value={data.onShortlist}
        onChange={(value) => {
          onResetAddress?.();
          onUpdateData({
            onShortlist: value.toString(),
            matchIdSelected: '',
            floorUnits: [],
            sizeValue: '',
            buildingTypes: [],
          });
        }}
      />
      {data.onShortlist === '0' && tenantOnScheduleContent}
      {data.onShortlist === '1' && tenantNotInScheduleContent}
    </div>
  );
};
export const RequirementRecordSpaceCard = withStyles(styles)(RequirementRecordSpaceCardComponent);
